import React, { useCallback, Dispatch, SetStateAction, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GetRowIdParams, GridReadyEvent, RowDragEndEvent } from 'ag-grid-community';
import { LandingCarousel, useStore } from '../../../../../store';
import CheckboxRenderer from '../../../../utility/agGrid/CheckboxRenderer';
import DeleteCarouselRenderer from '../../../../utility/agGrid/DeleteCarouselRenderer';
import { defaultCarousel } from '../constants/defaultCarousel';

interface CarouselGridProps {
  curCarousels: LandingCarousel[];
  setCurCarousels: Dispatch<SetStateAction<LandingCarousel[]>>;
  submit: (newArray: Array<LandingCarousel>) => Promise<void>;
  refetch: (getHidden?: boolean) => Promise<void>;
}

/**
 * Abstracted component to handle carousel grid functionality
 * @returns custom grid for carousels
 */
export const CarouselGrid: React.FC<CarouselGridProps> = ({ curCarousels, setCurCarousels, submit, refetch }) => {
  const gridRef: any = useRef();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const dispatchSetAlert = useStore((state) => state.dispatchSetAlert);
  
  const defaultColDef = {
    resizable: true,
    minWidth: 100,
  };
  
  const columnDefs: ColDef[] = [
    {
      field: 'image',
      headerName: 'Image',
      editable: true,
      rowDrag: true
    },
    {
      field: 'link',
      headerName: 'Path',
      editable: true,
      resizable: true
    },
    {
      field: 'headerText',
      headerName: 'Header',
      editable: true
    },
    {
      field: 'subHeaderText',
      headerName: 'Subheader',
      editable: true
    },
    {
      field: 'buttonText',
      headerName: 'Button',
      editable: true
    },
    {
      field: 'isNew',
      hide: true
    },
    {
      field: 'isHidden',
      headerName: 'Hidden',
      cellRenderer: CheckboxRenderer,
      minWidth: 50,
      maxWidth: 100
    },
    {
      field: 'delete',
      headerName: 'Delete',
      cellRenderer: (props: any) => DeleteCarouselRenderer(props, curCarousels, setCurCarousels, dispatchSetAlert),
      minWidth: 50,
      maxWidth: 100
    }
  ];
  
  const onFirstDataRendered = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit();
  }, []);

  const addCarousel = useCallback(() => {
    let newCarouselList = [...curCarousels];
    newCarouselList.unshift(defaultCarousel);
    setCurCarousels(newCarouselList);
  }, [curCarousels, setCurCarousels]);

  const onRowDragEnd = useCallback(
    (event: RowDragEndEvent) => {
      try {
        const { api } = event;
        const updatedRowData: any[] = [];
        api.forEachNodeAfterFilterAndSort((node) => {
          updatedRowData.push(node.data);
        });
        
        // Update the order numbers based on the new row order from the grid
        const updatedCarousels = updatedRowData.map((carousel, index) => ({
          ...carousel,
          orderNum: index
        }));
        
        // Use setTimeout to avoid focus issues during drag end
        setTimeout(() => {
          setCurCarousels(updatedCarousels);
        }, 0);
        
        console.log("ROW DRAG END - Updated carousels:", updatedCarousels);
      } catch (error) {
        console.warn("Row drag end error (non-critical):", error);
        // Fallback: just update order numbers without grid data
        const updatedCarousels = curCarousels.map((carousel, index) => ({
          ...carousel,
          orderNum: index
        }));
        setCurCarousels(updatedCarousels);
      }
    },
    [curCarousels, setCurCarousels]
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    // add id to each item, needed for immutable store to work
    const carouselsWithOrderNum = curCarousels.map((carousel, index) => ({...carousel}));
    setCurCarousels(carouselsWithOrderNum);
  }, [curCarousels, setCurCarousels]);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  return (
    <div style={{display: 'grid', gridTemplateRows: '5rem 1fr'}}>
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr'}}>
        <button className='btn btn--secondary' onClick={() => refetch(true)}>Reset</button>
        <button className='btn btn--secondary' onClick={() => addCarousel()}>Add</button>
        <button className='btn btn--secondary' onClick={() => submit(curCarousels)}>Submit</button>
      </div>
      <div className='ag-theme-alpine' style={gridStyle}>
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onRowDragEnd={onRowDragEnd}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          getRowId={getRowId}
          rowData={curCarousels}
          rowDragManaged={true}
          suppressMoveWhenRowDragging={true}
          stopEditingWhenCellsLoseFocus={true}
        />
      </div>
    </div>
  );
};
